<template>
  <div>
    <!-- Back Button -->
    <CButton color="secondary" size="sm" class="mb-3" @click="$router.back()">
      <CIcon name="cil-arrow-left" /> Back to Periods
    </CButton>

    <!-- Loading State -->
    <CCard v-if="loading">
      <CCardBody class="text-center py-5">
        <CSpinner color="primary" />
        <p class="mt-3">Loading period details...</p>
      </CCardBody>
    </CCard>

    <!-- Error State -->
    <CAlert v-else-if="error" color="danger">
      <h4>Error Loading Period</h4>
      <p>{{ error }}</p>
      <CButton color="danger" variant="outline" @click="loadPeriod">Try Again</CButton>
    </CAlert>

    <!-- Period Details -->
    <template v-else-if="period">
      <!-- Period Summary Card -->
      <CCard>
        <CCardHeader>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h3 class="mb-1">Tax Period: {{ period.periodCode }}</h3>
              <small class="text-muted">
                {{ formatDate(period.startDate) }} - {{ formatDate(period.endDate) }}
              </small>
            </div>
            <CBadge :color="getStatusColor(period.status)" size="lg">
              {{ getStatusLabel(period.status) }}
            </CBadge>
          </div>

          <!-- Sales and Purchases Breakdown -->
          <CRow class="mt-4">
            <!-- Sales Section -->
            <CCol lg="6" md="12" class="mb-3 mb-lg-0">
              <div class="border rounded p-3">
                <h5 class="text-danger mb-3">Sales</h5>
                <div class="d-flex justify-content-between mb-2">
                  <span>Total Sales:</span>
                  <strong>{{ formatCurrency(period.totalSalesAmount || 0) }}</strong>
                </div>
                <div class="d-flex justify-content-between mb-2">
                  <span>Tax (8%):</span>
                  <strong class="text-danger">{{ formatCurrency(period.totalOutputTax || 0) }}</strong>
                </div>
                <div class="d-flex justify-content-between pt-2 border-top">
                  <strong>Grand Total:</strong>
                  <strong class="h5 text-danger mb-0">{{ formatCurrency(totalSalesWithTax) }}</strong>
                </div>
              </div>
            </CCol>

            <!-- Purchases Section -->
            <CCol lg="6" md="12">
              <div class="border rounded p-3">
                <h5 class="text-success mb-3">Purchases</h5>
                <div class="d-flex justify-content-between mb-2">
                  <span>Total Purchases:</span>
                  <strong>{{ formatCurrency(period.totalPurchaseAmount || 0) }}</strong>
                </div>
                <div class="d-flex justify-content-between mb-2">
                  <span>
                    SST paid:
                    <CBadge v-if="!inputTaxClaimable" color="secondary" class="ml-1">Not claimable</CBadge>
                  </span>
                  <strong :class="inputTaxClaimable ? 'text-success' : 'text-muted'">
                    {{ formatCurrency(inputTaxPaid) }}
                  </strong>
                </div>
                <p v-if="!inputTaxClaimable && inputTaxPaid" class="text-muted small mb-2">
                  {{ notClaimableHint }}
                </p>
                <div class="d-flex justify-content-between pt-2 border-top">
                  <strong>Grand Total:</strong>
                  <strong class="h5 text-success mb-0">{{ formatCurrency(totalPurchasesWithTax) }}</strong>
                </div>
              </div>
            </CCol>
          </CRow>
        </CCardHeader>
        <CCardBody>
          <CRow class="text-center">
            <CCol md="3">
              <div class="text-muted small">Output Tax (Sales)</div>
              <div class="h2 text-danger">{{ formatCurrency(period.totalOutputTax) }}</div>
              <div class="text-muted small">{{ period.invoiceCount || 0 }} invoices</div>
            </CCol>
            <CCol md="3">
              <template v-if="deductsInputTax">
                <div class="text-muted small">Input tax (claimable)</div>
                <div class="h2 text-success">{{ formatCurrency(period.totalInputTax) }}</div>
              </template>
              <template v-else>
                <div class="text-muted small">
                  Input tax (purchases)
                  <CIcon
                    name="cil-info"
                    size="sm"
                    class="ml-1"
                    tabindex="0"
                    :aria-label="notClaimableHint"
                    v-c-tooltip="{ content: notClaimableHint, placement: 'top' }"
                  />
                </div>
                <div class="h2 text-muted">Not claimable</div>
                <div class="text-muted small">{{ formatCurrency(inputTaxPaid) }} SST paid</div>
              </template>
              <div class="text-muted small">{{ period.billCount || 0 }} bills</div>
            </CCol>
            <CCol md="3">
              <div class="text-muted small">Net Tax Payable</div>
              <div class="h2" :class="period.netTaxPayable < 0 ? 'text-success' : 'text-primary'">
                {{ formatCurrency(Math.abs(period.netTaxPayable)) }}
                <span v-if="period.netTaxPayable < 0" class="small">(Refundable)</span>
              </div>
            </CCol>
            <CCol md="3">
              <div class="text-muted small">Due Date</div>
              <div class="h3">{{ formatDate(period.dueDate) }}</div>
              <CBadge :color="getUrgencyColor(period)">
                {{ getUrgencyText(period) }}
              </CBadge>
            </CCol>
          </CRow>

          <CAlert v-if="totalsOutOfDate" color="info" class="mt-3 mb-0 small">
            The input tax setting changed after these totals were worked out. Press Recalculate to update this period.
          </CAlert>

          <hr>

          <div class="d-flex justify-content-end">
            <CButton color="info" size="sm" @click="$router.push(`/tenants/tax/sst03/${period.id}`)">
              <CIcon name="cil-file" /> View SST-03 Report
            </CButton>
            <CButton color="primary" size="sm" class="ml-2" @click="recalculate">
              <CIcon name="cil-reload" /> Recalculate
            </CButton>
          </div>
        </CCardBody>
      </CCard>

      <!-- Payment Summary -->
      <CCard>
        <CCardHeader>
          <div class="d-flex justify-content-between align-items-center">
            <strong>Payment Summary</strong>
            <CButton
              color="primary"
              size="sm"
              @click="openPaymentModal"
              :disabled="period.status === 3"
            >
              <CIcon name="cil-plus" /> Record Payment
            </CButton>
          </div>
        </CCardHeader>
        <CCardBody>
          <CRow class="text-center">
            <CCol md="3">
              <div class="text-muted small">Net Tax Payable</div>
              <div class="h3 text-primary">{{ formatCurrency(period.netTaxPayable) }}</div>
            </CCol>
            <CCol md="3">
              <div class="text-muted small">Penalty Amount</div>
              <div class="h3 text-warning">{{ formatCurrency(period.penaltyAmount || 0) }}</div>
            </CCol>
            <CCol md="3">
              <div class="text-muted small">Total Paid</div>
              <div class="h3 text-success">{{ formatCurrency(totalPaid) }}</div>
            </CCol>
            <CCol md="3">
              <div class="text-muted small">Amount Due</div>
              <div class="h3" :class="amountDue > 0 ? 'text-danger' : 'text-success'">
                {{ formatCurrency(Math.abs(amountDue)) }}
                <span v-if="amountDue <= 0" class="small">(Paid)</span>
              </div>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      <!-- Payment History -->
      <CCard v-if="payments.length > 0">
        <CCardHeader>
          <strong>Payment History</strong>
          <span class="ml-2 text-muted">({{ payments.length }} payments)</span>
        </CCardHeader>
        <CCardBody>
          <CDataTable
            :items="payments"
            :fields="paymentFields"
            hover
            striped
            sorter
          >
            <template #paymentDate="{item}">
              <td>
                {{ formatDate(item.paymentDate) }}
              </td>
            </template>

            <template #paymentNo="{item}">
              <td>
                <strong>{{ item.paymentNo }}</strong>
              </td>
            </template>

            <template #taxAmount="{item}">
              <td class="text-right">{{ formatCurrency(item.taxAmount) }}</td>
            </template>

            <template #penaltyAmount="{item}">
              <td class="text-right">{{ formatCurrency(item.penaltyAmount) }}</td>
            </template>

            <template #amount="{item}">
              <td class="text-right">
                <strong class="text-primary">{{ formatCurrency(item.amount) }}</strong>
              </td>
            </template>

            <template #paymentMethod="{item}">
              <td>
                <span>{{ item.paymentMethod }}</span>
                <br>
                <small class="text-muted">Ref: {{ item.paymentReference }}</small>
              </td>
            </template>

            <template #status="{item}">
              <td>
                <CBadge :color="getPaymentStatusColor(item.status)">
                  {{ getPaymentStatusLabel(item.status) }}
                </CBadge>
              </td>
            </template>

            <template #actions="{item}">
              <td>
                <CButton
                  v-if="item.status === 1"
                  color="danger"
                  size="sm"
                  variant="outline"
                  @click="confirmCancelPayment(item)"
                >
                  <CIcon name="cil-x" /> Cancel
                </CButton>
                <span v-else class="text-muted">-</span>
              </td>
            </template>
          </CDataTable>
        </CCardBody>
      </CCard>

      <!-- Transactions -->
      <CCard>
        <CCardHeader>
          <strong>Tax Transactions</strong>
          <span class="ml-2 text-muted">({{ transactions.length }} transactions)</span>
        </CCardHeader>
        <CCardBody>
          <CDataTable
            :items="transactions"
            :fields="transactionFields"
            hover
            striped
            sorter
          >
            <template #sourceDocumentNo="{item}">
              <td>
                <strong>{{ item.sourceDocumentNo }}</strong>
                <br>
                <small class="text-muted">{{ formatDate(item.transactionDate) }}</small>
              </td>
            </template>

            <template #companyName="{item}">
              <td>
                <span v-if="item.transactionType === 'OutputTax'">
                  {{ item.customerName || '-' }}
                </span>
                <span v-else>
                  {{ item.vendorName || '-' }}
                </span>
              </td>
            </template>

            <template #transactionType="{item}">
              <td>
                <CBadge :color="item.transactionType === 'OutputTax' ? 'danger' : 'success'">
                  {{ item.transactionType === 'OutputTax' ? 'Output' : 'Input' }}
                </CBadge>
                <div v-if="item.transactionType !== 'OutputTax' && !inputTaxClaimable" class="text-muted small">
                  Not claimable
                </div>
              </td>
            </template>

            <template #taxableAmount="{item}">
              <td class="text-right">{{ formatCurrency(item.taxableAmount) }}</td>
            </template>

            <template #taxAmount="{item}">
              <td class="text-right">
                <strong>{{ formatCurrency(item.taxAmount) }}</strong>
              </td>
            </template>

            <template #totalWithTax="{item}">
              <td class="text-right">
                <strong class="text-primary">
                  {{ formatCurrency((parseFloat(item.taxableAmount) || 0) + (parseFloat(item.taxAmount) || 0)) }}
                </strong>
              </td>
            </template>
          </CDataTable>
        </CCardBody>
      </CCard>

      <!-- Tax Payment Modal -->
      <TaxPaymentModal
        :show="showPaymentModal"
        :period="period"
        @update:show="showPaymentModal = $event"
        @payment-created="handlePaymentCreated"
      />

      <!-- Cancel payment confirm (UI-GUIDELINES 5.4) -->
      <CModal :show.sync="showCancelPayment" :close-on-backdrop="false" size="sm">
        <template #header>
          <h6 class="modal-title">Cancel payment {{ cancelTarget && cancelTarget.paymentNo }}?</h6>
        </template>
        <p class="mb-0">
          The payment of RM {{ formatMyrAmount(cancelTarget && cancelTarget.amount) }} will be cancelled and
          this period's paid amount reduced. This can't be undone.
        </p>
        <template #footer>
          <CButton color="light" @click="showCancelPayment = false">Keep payment</CButton>
          <CButton color="danger" @click="doCancelPayment">Cancel payment</CButton>
        </template>
      </CModal>
    </template>
  </div>
</template>

<script>
import TaxAPI from '@/lib/taxApi'
import TaxPaymentModal from '@/views/components/TaxPaymentModal.vue'
import { formatMyr } from '@/utils/money'

export default {
  name: 'TaxPeriod',
  components: {
    TaxPaymentModal
  },
  data() {
    return {
      loading: false,
      error: null,
      showCancelPayment: false,
      cancelTarget: null,
      period: null,
      transactions: [],
      payments: [],
      showPaymentModal: false,
      notClaimableHint: "SST paid on purchases is part of your cost and can't be deducted. Ask your tax agent if your business qualifies, then change it in Business settings.",
      transactionFields: [
        { key: 'sourceDocumentNo', label: 'Document' },
        { key: 'companyName', label: 'Company' },
        { key: 'transactionType', label: 'Type' },
        { key: 'taxableAmount', label: 'Taxable Amount' },
        { key: 'taxRateInPercentage', label: 'Rate (%)' },
        { key: 'taxAmount', label: 'Tax Amount' },
        { key: 'totalWithTax', label: 'Total with Tax' }
      ],
      paymentFields: [
        { key: 'paymentDate', label: 'Date' },
        { key: 'paymentNo', label: 'Payment No' },
        { key: 'taxAmount', label: 'Tax Amount' },
        { key: 'penaltyAmount', label: 'Penalty' },
        { key: 'amount', label: 'Total' },
        { key: 'paymentMethod', label: 'Method / Reference' },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: 'Actions' }
      ]
    }
  },
  computed: {
    // Total sales with tax (using backend pre-calculated values)
    totalSalesWithTax() {
      if (!this.period) return 0
      // TotalSalesAmount (taxable) + TotalOutputTax (tax) = Grand Total
      const taxableAmount = parseFloat(this.period.totalSalesAmount) || 0
      const taxAmount = parseFloat(this.period.totalOutputTax) || 0
      return taxableAmount + taxAmount
    },

    // Business setting (Business.SstInputTaxClaimable), sent with the period detail
    inputTaxClaimable() {
      return !!this.period?.inputTaxClaimable
    },

    // SST paid on purchases in the period, claimable or not
    inputTaxPaid() {
      if (!this.period) return 0
      const paid = this.period.inputTaxPaid
      return parseFloat(paid != null ? paid : this.period.totalInputTax) || 0
    },

    // Show input tax as a deduction when it is claimable, or when the API's totals still deduct it
    // (a period not recalculated since the setting changed). Net payable always comes from the API.
    deductsInputTax() {
      return this.inputTaxClaimable || (parseFloat(this.period?.totalInputTax) || 0) !== 0
    },

    // The period's TotalInputTax should be InputTaxPaid when claimable and 0 when not
    totalsOutOfDate() {
      if (!this.period || this.period.inputTaxPaid == null) return false
      const expected = this.inputTaxClaimable ? this.inputTaxPaid : 0
      return Math.abs(expected - (parseFloat(this.period.totalInputTax) || 0)) >= 0.005
    },

    // Total purchases with tax (using backend pre-calculated values)
    totalPurchasesWithTax() {
      if (!this.period) return 0
      // TotalPurchaseAmount (taxable) + SST paid on purchases = Grand Total
      const taxableAmount = parseFloat(this.period.totalPurchaseAmount) || 0
      return taxableAmount + this.inputTaxPaid
    },

    // Total paid amount (sum of completed payments)
    totalPaid() {
      if (!this.payments || this.payments.length === 0) return 0
      return this.payments
        .filter(p => p.status === 1) // Status 1 = Completed
        .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)
    },

    // Amount due (total due - total paid)
    amountDue() {
      if (!this.period) return 0
      const netTax = parseFloat(this.period.netTaxPayable) || 0
      const penalty = parseFloat(this.period.penaltyAmount) || 0
      const totalDue = netTax + penalty
      return totalDue - this.totalPaid
    }
  },
  async mounted() {
    this.$store.commit("setPageNavItems", ["Tax Management", "Tax Periods"])
    await this.loadPeriod()
  },
  methods: {
    async loadPeriod() {
      this.loading = true
      this.error = null

      try {
        const taxApi = new TaxAPI(window.localStorage.getItem('token'))
        const periodId = this.$route.params.id

        // Get period details
        const periodResponse = await taxApi.getPeriod(periodId)
        this.period = periodResponse.data.result

        // Get transactions
        const transactionsResponse = await taxApi.getTransactionsByPeriod(periodId)
        this.transactions = transactionsResponse.data.result || []

        // Get payments
        await this.loadPayments()
      } catch (error) {
        console.error('Error loading period:', error)
        this.error = error.response?.data?.message || 'Failed to load period'
      } finally {
        this.loading = false
      }
    },

    async loadPayments() {
      try {
        const taxApi = new TaxAPI(window.localStorage.getItem('token'))
        const periodId = this.$route.params.id
        const paymentsResponse = await taxApi.getPaymentsByPeriod(periodId)
        this.payments = paymentsResponse.data.result || []
      } catch (error) {
        console.error('Error loading payments:', error)
        // Don't show error toast for payments - it's not critical
      }
    },

    async recalculate() {
      try {
        const taxApi = new TaxAPI(window.localStorage.getItem('token'))
        await taxApi.recalculatePeriod(this.period.id)
        this.$toast.success('Period recalculated')
        await this.loadPeriod()
      } catch (error) {
        this.$toast.error("Couldn't recalculate the period. Check your connection and try again.")
      }
    },

    openPaymentModal() {
      this.showPaymentModal = true
    },

    async handlePaymentCreated(payment) {
      // Reload period and payments to reflect changes
      await this.loadPeriod()
      this.$toast.success(`Payment ${payment.paymentNo} recorded`)
    },

    confirmCancelPayment(payment) {
      this.cancelTarget = payment
      this.showCancelPayment = true
    },

    doCancelPayment() {
      const payment = this.cancelTarget
      this.showCancelPayment = false
      if (payment) this.cancelPayment(payment.id)
    },

    formatMyrAmount(value) {
      return formatMyr(value)
    },

    async cancelPayment(paymentId) {
      try {
        const taxApi = new TaxAPI(window.localStorage.getItem('token'))
        await taxApi.cancelPayment(paymentId)
        this.$toast.success('Payment cancelled')
        await this.loadPeriod()
      } catch (error) {
        console.error('Error cancelling payment:', error)
        let errorMessage = "Couldn't cancel the payment. Check your connection and try again."
        if (error.response?.data?.message) {
          errorMessage = "Couldn't cancel the payment. " + error.response.data.message
        }
        this.$toast.error(errorMessage)
      }
    },

    getPaymentStatusLabel(status) {
      const labels = {
        0: 'Pending',
        1: 'Completed',
        2: 'Cancelled',
        3: 'Failed'
      }
      return labels[status] || 'Unknown'
    },

    getPaymentStatusColor(status) {
      const colors = {
        0: 'warning',   // Pending
        1: 'success',   // Completed
        2: 'secondary', // Cancelled
        3: 'danger'     // Failed
      }
      return colors[status] || 'secondary'
    },

    formatCurrency(amount) {
      return `RM ${formatMyr(amount)}`
    },

    formatDate(date) {
      const taxApi = new TaxAPI()
      return taxApi.formatDate(date)
    },

    getStatusLabel(status) {
      const taxApi = new TaxAPI()
      return taxApi.getStatusLabel(status)
    },

    getStatusColor(status) {
      const taxApi = new TaxAPI()
      return taxApi.getStatusColor(status)
    },

    getUrgencyColor(period) {
      const taxApi = new TaxAPI()
      return taxApi.getUrgencyBadge(period).color
    },

    getUrgencyText(period) {
      const taxApi = new TaxAPI()
      return taxApi.getUrgencyBadge(period).text
    }
  }
}
</script>